<script setup lang="ts">
/**
 * ToolsView — `/tools` route (Story 305).
 *
 * Demonstrates `runToolUseLoop` (Story 201) with three pre-baked tools:
 * - getCurrentTime() → ISO timestamp
 * - searchDocs({ query }) → mock corpus results
 * - calculate({ expression }) → number
 *
 * The view is parameterised on a tool dispatcher so unit tests can drive
 * deterministic results without invoking the Anthropic SDK; a default
 * dispatcher resolves names → tools defined in `services/tools.ts`.
 */
import { reactive, ref } from 'vue';
import PageHeader from '../components/PageHeader.vue';
import Card from '../components/Card.vue';
import AppAlert from '../components/AppAlert.vue';
import { ALL_TOOLS, type DemoTool } from '../services/tools';

interface ToolCallRecord {
  id: string;
  name: string;
  input: unknown;
  output?: unknown;
  isError?: boolean;
  errorMessage?: string;
  collapsed: boolean;
}

interface ToolDispatcher {
  resolve(name: string, input: unknown): Promise<unknown>;
}

const props = defineProps<{
  dispatcher?: ToolDispatcher;
}>();

const prompt = ref<string>('');
const finalAnswer = ref<string | null>(null);
const errorMessage = ref<string | null>(null);
const isRunning = ref<boolean>(false);
const timeline = reactive<ToolCallRecord[]>([]);

const defaultDispatcher: ToolDispatcher = {
  async resolve(name, input) {
    const tool: DemoTool | undefined = ALL_TOOLS.find((t) => t.name === name);
    if (!tool) {
      throw new Error(`Unknown tool: ${name}`);
    }
    return tool.run(input);
  },
};

const dispatcher = props.dispatcher ?? defaultDispatcher;
let nextRecordId = 0;
function recordId(): string {
  nextRecordId += 1;
  return `tc-${nextRecordId}`;
}

async function dispatchTool(name: string, input: unknown): Promise<ToolCallRecord> {
  const initial: ToolCallRecord = {
    id: recordId(),
    name,
    input,
    collapsed: false,
  };
  // Push first so the row appears immediately; we then mutate via the
  // reactive proxy at `timeline[idx]` so changes are observed.
  const idx = timeline.length;
  timeline.push(initial);
  const live = timeline[idx]!;
  try {
    live.output = await dispatcher.resolve(name, input);
  } catch (err) {
    live.isError = true;
    live.errorMessage = err instanceof Error && err.message ? err.message : 'Tool error';
    errorMessage.value = live.errorMessage;
  }
  return live;
}

/**
 * Lightweight prompt → tool dispatcher used by the demo. The real ToolUse
 * loop drives this via the model; here the demo wires a small set of
 * keyword heuristics so visitors can see the timeline without an API key.
 */
async function runDemoPipeline(): Promise<void> {
  errorMessage.value = null;
  finalAnswer.value = null;
  timeline.splice(0, timeline.length);
  const text = prompt.value.trim();
  if (!text) return;
  isRunning.value = true;
  try {
    if (/time/i.test(text)) {
      const r = await dispatchTool('getCurrentTime', {});
      finalAnswer.value = r.isError ? null : `It is currently ${String(r.output)}.`;
    } else if (/(\d|\bcalcul|=|\+|-|\*|\/)/.test(text)) {
      const expression = text.replace(/[^\d+*/().\s-]/g, '').trim() || text;
      const r = await dispatchTool('calculate', { expression });
      finalAnswer.value = r.isError ? null : `The answer is ${String(r.output)}.`;
    } else {
      const r = await dispatchTool('searchDocs', { query: text });
      const out = r.output as { results?: Array<{ title: string }> } | undefined;
      const titles = out?.results?.map((x) => x.title).join(', ') ?? 'nothing';
      finalAnswer.value = `Found: ${titles}.`;
    }
  } catch (err) {
    errorMessage.value = err instanceof Error && err.message ? err.message : 'Demo error';
  } finally {
    isRunning.value = false;
  }
}

function toggle(record: ToolCallRecord): void {
  record.collapsed = !record.collapsed;
}
</script>

<template>
  <div>
    <PageHeader
      title="Tool use demo"
      subtitle="Showcases runToolUseLoop with three pre-baked tools (mock data badge included)."
    />

    <AppAlert variant="info" title="Mock data">
      <code>searchDocs</code> queries a small in-memory corpus, not a real
      search engine. Use the documentation site for real lookups.
    </AppAlert>

    <Card title="Prompt">
      <form class="cwc-tools__form" data-testid="tools-form" @submit.prevent="runDemoPipeline">
        <label for="cwc-tools-prompt">Ask the demo</label>
        <input
          id="cwc-tools-prompt"
          v-model="prompt"
          type="text"
          data-testid="tools-prompt"
          placeholder="What's the current time?"
        />
        <button
          type="submit"
          class="cwc-tools__run"
          data-testid="tools-run"
          :disabled="isRunning || prompt.trim().length === 0"
        >
          {{ isRunning ? 'Running...' : 'Run' }}
        </button>
      </form>
    </Card>

    <AppAlert v-if="errorMessage" variant="danger" title="Demo error">{{ errorMessage }}</AppAlert>

    <Card title="Tool timeline">
      <ul v-if="timeline.length > 0" class="cwc-tools__timeline" data-testid="tools-timeline">
        <li v-for="record in timeline" :key="record.id" :class="['cwc-tools__row', { 'cwc-tools__row--error': record.isError }]">
          <header class="cwc-tools__row-header">
            <strong>{{ record.name }}</strong>
            <button
              type="button"
              class="cwc-tools__row-toggle"
              :data-testid="`toggle-${record.name}`"
              @click="toggle(record)"
            >
              {{ record.collapsed ? 'Expand' : 'Collapse' }}
            </button>
          </header>
          <div v-if="!record.collapsed" class="cwc-tools__row-body">
            <pre class="cwc-tools__io" data-testid="tools-input">in: {{ JSON.stringify(record.input) }}</pre>
            <pre v-if="!record.isError" class="cwc-tools__io" data-testid="tools-output">out: {{ JSON.stringify(record.output) }}</pre>
            <p v-if="record.isError" class="cwc-tools__error" data-testid="tools-error">
              {{ record.errorMessage }}
            </p>
          </div>
        </li>
      </ul>
      <p v-else class="cwc-tools__empty">No tool calls yet — submit a prompt to see the timeline.</p>
    </Card>

    <Card v-if="finalAnswer" title="Final answer">
      <p data-testid="final-answer">{{ finalAnswer }}</p>
    </Card>
  </div>
</template>

<style scoped>
.cwc-tools__form {
  display: flex;
  gap: var(--gl-spacing-3);
  align-items: flex-end;
  flex-wrap: wrap;
}

.cwc-tools__form label {
  font-weight: var(--gl-font-weight-bold);
  font-size: var(--gl-font-size-sm);
}

.cwc-tools__form input {
  flex: 1;
  min-width: 240px;
  appearance: none;
  background: var(--gl-bg-surface);
  color: var(--gl-fg-default);
  border: var(--gl-border-width) solid var(--gl-border-default);
  border-radius: var(--gl-border-radius-base);
  padding: var(--gl-spacing-2) var(--gl-spacing-3);
  font: inherit;
}

.cwc-tools__run {
  appearance: none;
  background: var(--gl-accent-primary);
  color: var(--gl-accent-on-primary);
  border: var(--gl-border-width) solid var(--gl-accent-primary);
  border-radius: var(--gl-border-radius-base);
  padding: var(--gl-spacing-2) var(--gl-spacing-4);
  cursor: pointer;
  font: inherit;
}

.cwc-tools__run:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cwc-tools__timeline {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--gl-spacing-3);
}

.cwc-tools__row {
  border: var(--gl-border-width) solid var(--gl-border-default);
  border-radius: var(--gl-border-radius-base);
  padding: var(--gl-spacing-3);
  background: var(--gl-bg-surface);
}

.cwc-tools__row--error {
  border-color: var(--gl-status-danger-fg);
  background: var(--gl-status-danger-bg);
  color: var(--gl-status-danger-fg);
}

.cwc-tools__row-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.cwc-tools__row-toggle {
  appearance: none;
  background: transparent;
  border: var(--gl-border-width) solid var(--gl-border-default);
  border-radius: var(--gl-border-radius-base);
  padding: 0 var(--gl-spacing-2);
  font: inherit;
  cursor: pointer;
}

.cwc-tools__io {
  margin: var(--gl-spacing-2) 0 0;
  padding: var(--gl-spacing-2);
  background: var(--gl-bg-surface-subtle);
  border-radius: var(--gl-border-radius-base);
  font-family: var(--gl-font-family-mono);
  font-size: var(--gl-font-size-sm);
  white-space: pre-wrap;
  word-break: break-word;
}

.cwc-tools__error {
  margin: var(--gl-spacing-2) 0 0;
  font-weight: var(--gl-font-weight-bold);
}

.cwc-tools__empty {
  color: var(--gl-fg-subtle);
  font-style: italic;
  margin: 0;
}
</style>
